import { formatDate } from "@/composables/formatUtils";
import type { ChatTurnCorrection } from "@/dashboard/inventory_management/common/aiChatTypes";
import {
  ALL_COLUMNS,
  type ColumnKey,
} from "@/dashboard/inventory_management/common/portfolioTableColumns";
import { BeneficiaryTypeEnum } from "@/types";

type Translate = (key: string, values?: Record<string, string>) => string;

/** The value each per-order setting takes in a CANCEL_SHARED_SETTING_CONFLICT correction. */
interface SettingValues {
  beneficiary_type: BeneficiaryTypeEnum;
  consumption_period: { start: string | null; end: string | null };
  /** The editor's template: each statement's own name reads as {beneficiary_name}. */
  cancellation_purpose: string;
}
type Setting = keyof SettingValues;

/** @property number - 1-based position in the one uploaded document; null across several documents or in a chat draft */
interface StatementRef {
  number: number | null;
  beneficiary_name: string;
}

/**
 * The statements sharing one value of a setting.
 *
 * @property value - null when the statements state no value
 * @property statements - The first of the `count` statements, as many as the backend names
 */
interface ValueGroup<V> {
  value: V | null;
  count: number;
  statements: StatementRef[];
}

/**
 * One setting the statements disagree on, against its most common stated value.
 *
 * @property differing - The first other values, in document order
 * @property more_differing_count - Statements holding values beyond the listed ones
 */
interface SettingConflict<S extends Setting = Setting> {
  setting: S;
  common_value: SettingValues[S];
  common_count: number;
  differing: ValueGroup<SettingValues[S]>[];
  more_differing_count: number;
}

/** Where the statements came from: an upload's own document, or a chat draft combined with a turn. */
type SettingConflictContext = "uploaded_documents" | "draft";

interface SettingRenderer<V> {
  label: string;
  isValue: (value: unknown) => value is V;
  render: (value: V, translate: Translate) => string;
}

const CRITERION_COLUMNS: Record<string, ColumnKey> = {
  technology_types: "technology_type",
  production_period_start: "production_month",
  production_period_end: "production_month",
  issuing_regions: "issuing_region",
  registry_regions: "registry_region",
  cod_age: "cod_age",
  production_support: "production_support",
  ecolabels: "ecolabel_tags",
  production_device_names: "production_device_name",
  portfolio_names: "portfolio_name",
};

const CONFLICT_CONTEXTS = new Set([
  "excel_rows",
  "uploaded_documents",
  "draft_and_document",
]);

// Labels and values read as the cancellation editor names them, so the customer finds the same words there.
const SETTINGS: { [S in Setting]: SettingRenderer<SettingValues[S]> } = {
  beneficiary_type: {
    label: "go_cancel.form.beneficiary_type.label",
    isValue: (value): value is BeneficiaryTypeEnum =>
      Object.values<unknown>(BeneficiaryTypeEnum).includes(value),
    render: (type, translate) =>
      translate(`go_cancel.form.beneficiary_type.${type}`),
  },
  consumption_period: {
    label: "go_cancel.summary_consumption_period",
    isValue: (value): value is SettingValues["consumption_period"] =>
      isRecord(value) &&
      isOptionalText(value.start) &&
      isOptionalText(value.end),
    render: ({ start, end }, translate) => {
      const notSpecified = translate("common.not_specified");
      return `${formatDate(start) || notSpecified} – ${formatDate(end) || notSpecified}`;
    },
  },
  cancellation_purpose: {
    label: "go_cancel.form.cancellation_purpose.label",
    isValue: (value): value is string => typeof value === "string",
    render: (purpose) => `"${purpose}"`,
  },
};

/**
 * Translate recognized correction metadata; never render arbitrary server error messages. The
 * only server text shown is what the user's own document said (beneficiary names, purposes).
 */
export const getCancelAiCorrection = (
  { error_type, error_data }: ChatTurnCorrection,
  translate: Translate
): string | null => {
  if (error_type === "CANCEL_CERTIFICATE_CRITERIA_CONFLICT")
    return describeCriteriaConflict(error_data, translate);
  if (error_type === "CANCEL_SHARED_SETTING_CONFLICT") {
    const parsed = parseSharedSettingConflicts(error_data);
    return parsed && describeSharedSettingConflicts(parsed, translate);
  }
  return null;
};

const describeCriteriaConflict = (
  errorData: unknown,
  translate: Translate
): string | null => {
  const { context, criterion }: Record<string, unknown> = isRecord(errorData)
    ? errorData
    : {};
  if (
    typeof context !== "string" ||
    !CONFLICT_CONTEXTS.has(context) ||
    typeof criterion !== "string" ||
    !Object.hasOwn(CRITERION_COLUMNS, criterion)
  )
    return null;
  return translate("ai_chat.certificate_criteria_conflict", {
    context: translate(`ai_chat.conflict_contexts.${context}`),
    criterion: translate(ALL_COLUMNS[CRITERION_COLUMNS[criterion]].header),
  });
};

/** Null when anything has an unexpected shape, so the chat falls back to its generic reply. */
const parseSharedSettingConflicts = (
  errorData: unknown
): { context: SettingConflictContext; conflicts: SettingConflict[] } | null => {
  if (!isRecord(errorData)) return null;
  const { context, conflicts } = errorData;
  return isSettingConflictContext(context) &&
    Array.isArray(conflicts) &&
    conflicts.length > 0 &&
    conflicts.every(isSettingConflict)
    ? { context, conflicts }
    : null;
};

/** Lists each differing value with its statements, then the value the other statements share. */
const describeSharedSettingConflicts = (
  {
    context,
    conflicts,
  }: { context: SettingConflictContext; conflicts: SettingConflict[] },
  translate: Translate
): string =>
  [
    translate("ai_chat.shared_setting_conflict.intro"),
    ...conflicts.map((conflict) =>
      describeSettingConflict(conflict, translate)
    ),
    translate(`ai_chat.shared_setting_conflict.outro.${context}`),
  ].join("\n\n");

const describeSettingConflict = <S extends Setting>(
  conflict: SettingConflict<S>,
  translate: Translate
): string => {
  const { label, render } = SETTINGS[conflict.setting];
  const describe = (value: SettingValues[S] | null) =>
    value === null
      ? translate("common.not_specified")
      : render(value, translate);
  const more =
    conflict.more_differing_count > 0
      ? [`• ${moreStatements(conflict.more_differing_count, translate)}`]
      : [];
  const others = translate("ai_chat.shared_setting_conflict.other_statements", {
    count: String(conflict.common_count),
  });
  return [
    `${translate(label)}:`,
    ...conflict.differing.map(
      (group) =>
        `• ${statementList(group, translate)}: ${describe(group.value)}`
    ),
    ...more,
    `• ${others}: ${describe(conflict.common_value)}`,
  ].join("\n");
};

const statementList = (
  { count, statements }: ValueGroup<unknown>,
  translate: Translate
): string => {
  const names = statements.map(statementLabel).join(", ");
  const unnamed = count - statements.length;
  return unnamed > 0 ? `${names} ${moreStatements(unnamed, translate)}` : names;
};

const statementLabel = ({ number, beneficiary_name }: StatementRef): string =>
  [number === null ? "" : `#${number}`, beneficiary_name]
    .filter(Boolean)
    .join(" ") || "—";

const moreStatements = (count: number, translate: Translate) =>
  translate("ai_chat.shared_setting_conflict.more_statements", {
    count: String(count),
  });

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isOptionalText = (value: unknown): value is string | null =>
  value === null || typeof value === "string";

const isSetting = (value: unknown): value is Setting =>
  typeof value === "string" && Object.hasOwn(SETTINGS, value);

const isSettingConflictContext = (
  value: unknown
): value is SettingConflictContext =>
  value === "uploaded_documents" || value === "draft";

const isSettingConflict = (value: unknown): value is SettingConflict => {
  if (!isRecord(value) || !isSetting(value.setting)) return false;
  const { isValue } = SETTINGS[value.setting];
  return (
    isValue(value.common_value) &&
    Number.isInteger(value.common_count) &&
    Number.isInteger(value.more_differing_count) &&
    Array.isArray(value.differing) &&
    value.differing.every(
      (group) =>
        isRecord(group) &&
        (group.value === null || isValue(group.value)) &&
        Number.isInteger(group.count) &&
        Array.isArray(group.statements) &&
        group.statements.every(isStatementRef)
    )
  );
};

const isStatementRef = (value: unknown): value is StatementRef =>
  isRecord(value) &&
  (value.number === null || Number.isInteger(value.number)) &&
  typeof value.beneficiary_name === "string";
